import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Info, Save, Trash2, Loader2, Cog, Flag, CheckCircle2, X, Rocket, KeyRound, Copy, Eye, EyeOff } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TenantStatusBadge, TenantPackageBadge } from "./tenants/TenantBadges";
import DeleteTenantDialog from "./tenants/DeleteTenantDialog";
import { validateSlug } from "./tenants/slug";
import TenantOverrideEditor from "./features/TenantOverrideEditor";
import TenantTemplateSection from "./templates/TenantTemplateSection";
import TenantDesignSwitchSection from "./templates/TenantDesignSwitchSection";

function Row({ label, value, testId }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-2 border-t border-white/5 first:border-t-0" data-testid={testId}>
      <div className="text-[11px] text-white/40 uppercase tracking-wider self-center">{label}</div>
      <div className="col-span-2 text-sm text-white/90 break-all">{value || <span className="text-white/30">—</span>}</div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function PlatformTenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enums, setEnums] = useState({ statuses: ["DRAFT", "ACTIVE", "INACTIVE", "SUSPENDED", "ARCHIVED"], packages: ["STARTER", "GROWTH", "ENTERPRISE"] });
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showCreatedBanner, setShowCreatedBanner] = useState(searchParams.get("created") === "1");
  const [showLaunchedBanner, setShowLaunchedBanner] = useState(searchParams.get("launched") === "1");
  const [resetPwState, setResetPwState] = useState({ open: false, loading: false, temp: null, showPw: false });

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/platform/tenants/${id}`);
      setT(r.data);
      setEdit({
        tenant_name: r.data.tenant_name || "",
        tenant_slug: r.data.tenant_slug || "",
        tenant_status: r.data.tenant_status || "ACTIVE",
        package: r.data.package || "STARTER",
        owner_name: r.data.owner_name || "",
        owner_email: r.data.owner_email || "",
        owner_phone: r.data.owner_phone || "",
        notes: r.data.notes || "",
      });
    } catch (e) {
      notifyError(e, "Gagal memuat tenant");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    api.get("/platform/tenants/enums").then((r) => setEnums(r.data)).catch(() => {});
  }, [id, load]);

  const slugError = edit ? validateSlug(edit.tenant_slug) : null;
  const canSave = edit && edit.tenant_name.trim() && !slugError && !t?.deleted_at;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const r = await api.put(`/platform/tenants/${id}`, edit);
      setT(r.data.tenant);
      toast.success("Perubahan tenant disimpan.");
    } catch (e) {
      notifyError(e, "Gagal menyimpan perubahan");
    } finally {
      setSaving(false);
    }
  };

  const setField = (patch) => setEdit((e) => ({ ...e, ...patch }));

  const doResetPassword = async () => {
    if (resetPwState.loading) return;
    if (!window.confirm("Reset password admin tenant ini? Password lama akan tidak berlaku setelah reset.")) return;
    setResetPwState((s) => ({ ...s, loading: true }));
    try {
      const r = await api.post(`/platform/tenants/${id}/admin/reset-password`);
      setResetPwState({ open: true, loading: false, temp: r.data.temporary_password, showPw: false, email: r.data.tenant_admin?.email });
      toast.success("Password admin di-reset. Simpan password baru sekarang.");
    } catch (e) {
      setResetPwState({ open: false, loading: false, temp: null, showPw: false });
      notifyError(e, "Gagal reset password admin");
    }
  };

  const copyResetPassword = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} disalin`);
    } catch {
      toast.error("Gagal menyalin");
    }
  };

  return (
    <PlatformLayout title={t?.tenant_name || "Tenant"}>
      <div data-testid="platform-tenant-detail">
        <Link to="/platform/tenants" className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white mb-4" data-testid="tenant-detail-back">
          <ArrowLeft size={12} /> Kembali ke Tenant List
        </Link>

        {showCreatedBanner && (
          <div
            className="mb-4 rounded-2xl border border-emerald-500/40 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 p-4 flex items-start gap-3"
            data-testid="tenant-created-banner"
          >
            <div className="w-10 h-10 rounded-full bg-emerald-500/25 border border-emerald-500/50 grid place-items-center shrink-0">
              <CheckCircle2 className="text-emerald-300" size={20} />
            </div>
            <div className="flex-1">
              <div className="text-emerald-200 font-display font-black text-sm">Tenant berhasil dibuat dari template</div>
              <div className="text-emerald-100/70 text-xs mt-0.5">
                Status awal: <strong>DRAFT</strong>. Konfigurasi awal sudah di-copy dari template. Untuk go-live, gunakan
                <strong> Launch Center</strong> atau atur konfigurasi terlebih dahulu.
              </div>
            </div>
            <button
              onClick={() => {
                setShowCreatedBanner(false);
                const next = new URLSearchParams(searchParams);
                next.delete("created");
                setSearchParams(next, { replace: true });
              }}
              className="text-emerald-200 hover:text-white p-1"
              data-testid="tenant-created-banner-close"
              aria-label="Tutup banner"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {showLaunchedBanner && (
          <div
            className="mb-4 rounded-2xl border border-emerald-500/40 bg-gradient-to-r from-emerald-500/15 to-cyan-500/15 p-5 flex items-start gap-3"
            data-testid="tenant-launched-banner"
          >
            <div className="w-12 h-12 rounded-full bg-emerald-500/30 border border-emerald-500/60 grid place-items-center shrink-0">
              <Rocket className="text-emerald-200" size={22} />
            </div>
            <div className="flex-1">
              <div className="text-emerald-100 font-display font-black text-base">✅ Website berhasil di-launch</div>
              <div className="text-emerald-100/80 text-xs mt-1">
                Status: <strong>ONLINE</strong>.
                {t?.tenant_slug && (
                  <>
                    {" "}Website URL: <a
                      href={`https://${t.tenant_slug}.dynicty.com`}
                      target="_blank" rel="noopener noreferrer"
                      className="underline text-cyan-200 hover:text-white"
                      data-testid="tenant-launched-website-link"
                    >{`https://${t.tenant_slug}.dynicty.com`}</a>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={() => {
                setShowLaunchedBanner(false);
                const next = new URLSearchParams(searchParams);
                next.delete("launched");
                setSearchParams(next, { replace: true });
              }}
              className="text-emerald-200 hover:text-white p-1"
              data-testid="tenant-launched-banner-close"
              aria-label="Tutup banner"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {loading ? (
          <div className="text-white/40">Memuat…</div>
        ) : !t ? (
          <div className="text-rose-400">Tenant tidak ditemukan.</div>
        ) : (
          <>
            {/* Header */}
            <Card className="bg-white/5 border-white/10 p-5 mb-4 rounded-2xl">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="w-14 h-14 rounded-2xl grid place-items-center" style={{ backgroundColor: "color-mix(in srgb, var(--brand-primary) 25%, transparent)" }}>
                  <span className="text-2xl font-black brand-text-primary">{(t.tenant_name || "S")[0]}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-display font-black text-2xl truncate">{t.tenant_name}</div>
                  <div className="text-xs text-white/40 mt-0.5 font-mono">{t.tenant_slug} · {t.tenant_code}</div>
                </div>
                <div className="flex items-center gap-2">
                  <TenantStatusBadge status={t.tenant_status} />
                  <TenantPackageBadge package={t.package} />
                </div>
              </div>
              {t.deleted_at && (
                <div className="mt-3 text-[11px] text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
                  Tenant ini sudah <strong>dihapus (soft delete)</strong> pada {formatDate(t.deleted_at)} oleh {t.deleted_by || "unknown"}. Field readonly.
                </div>
              )}
              {t.missing_subscription && !t.deleted_at && (
                <div
                  className="mt-3 text-[12px] text-rose-200 bg-rose-500/10 border border-rose-500/40 rounded-lg px-3 py-2.5 flex items-center gap-2 flex-wrap"
                  data-testid="tenant-missing-subscription-warning"
                >
                  <span>
                    <strong className="uppercase tracking-wider">Tanpa Langganan</strong> — tenant ini aktif tapi tidak punya subscription valid.
                    Entitlement berjalan dari package saja; tidak ada penagihan/expiry.
                  </span>
                  <Link
                    to="/platform/subscriptions"
                    className="ml-auto inline-flex items-center px-2.5 py-1 rounded-md bg-rose-500/20 border border-rose-500/40 text-rose-100 font-bold hover:bg-rose-500/30"
                    data-testid="tenant-missing-subscription-fix-link"
                  >
                    Buat Subscription →
                  </Link>
                </div>
              )}
            </Card>

            {/* Launch Website CTA — hanya muncul saat tenant DRAFT */}
            {t.tenant_status === "DRAFT" && !t.deleted_at && (
              <Card
                className="bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-emerald-500/10 border-cyan-500/40 p-6 mb-4 rounded-2xl"
                data-testid="tenant-launch-cta"
              >
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-purple-500 grid place-items-center shrink-0 shadow-lg shadow-cyan-500/30">
                    <Rocket size={28} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-display font-black text-lg">Siap Go Live?</div>
                    <div className="text-white/70 text-sm mt-0.5">
                      Tenant ini masih <strong className="text-cyan-300">DRAFT</strong>. Jalankan One Click Launch untuk provision + deploy + bind domain otomatis.
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate(`/platform/launch?tenant_id=${t.tenant_id}`)}
                    className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:opacity-90 text-white font-black text-base px-6 py-6 shadow-lg shadow-cyan-500/25"
                    data-testid="tenant-launch-website-btn"
                  >
                    <Rocket size={18} className="mr-2" /> Launch Website
                  </Button>
                </div>
              </Card>
            )}

            {/* Status Banner untuk LAUNCHING / FAILED */}
            {t.tenant_status === "LAUNCHING" && !t.deleted_at && (
              <Card className="bg-cyan-500/10 border-cyan-500/40 p-4 mb-4 rounded-2xl" data-testid="tenant-launching-banner">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 text-cyan-300 animate-spin" />
                  <div className="flex-1">
                    <div className="text-cyan-200 font-display font-black text-sm">Launch sedang berjalan…</div>
                    <div className="text-cyan-100/70 text-xs mt-0.5">Buka Launch Center untuk memantau progress pipeline.</div>
                  </div>
                  <Button
                    onClick={() => navigate(`/platform/launch?tenant_id=${t.tenant_id}`)}
                    variant="outline"
                    className="border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/20"
                    data-testid="tenant-open-launch-center"
                  >
                    Open Launch Center
                  </Button>
                </div>
              </Card>
            )}

            {t.tenant_status === "FAILED" && !t.deleted_at && (
              <Card className="bg-rose-500/10 border-rose-500/40 p-4 mb-4 rounded-2xl" data-testid="tenant-failed-banner">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-rose-500/40 grid place-items-center">
                    <X size={12} className="text-rose-200" />
                  </div>
                  <div className="flex-1">
                    <div className="text-rose-200 font-display font-black text-sm">Launch gagal</div>
                    <div className="text-rose-100/70 text-xs mt-0.5">Buka Launch Center untuk retry atau rollback.</div>
                  </div>
                  <Button
                    onClick={() => navigate(`/platform/launch?tenant_id=${t.tenant_id}`)}
                    className="bg-rose-500 hover:bg-rose-600 text-white"
                    data-testid="tenant-retry-launch-btn"
                  >
                    <Rocket size={14} className="mr-1" /> Retry Launch
                  </Button>
                </div>
              </Card>
            )}

            {/* Edit Form */}
            <Card className="bg-white/5 border-white/10 p-5 mb-4 rounded-2xl">
              <h3 className="text-sm font-display font-bold text-white mb-4 flex items-center gap-2">
                <Cog size={14} /> Edit Tenant
              </h3>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Nama Tenant</label>
                  <Input
                    value={edit.tenant_name}
                    onChange={(e) => setField({ tenant_name: e.target.value })}
                    disabled={!!t.deleted_at}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="edit-tenant-name"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Slug</label>
                  <Input
                    value={edit.tenant_slug}
                    onChange={(e) => setField({ tenant_slug: e.target.value.toLowerCase() })}
                    disabled={!!t.deleted_at}
                    className="bg-white/5 border-white/10 text-white font-mono"
                    data-testid="edit-tenant-slug"
                  />
                  {slugError && <div className="text-[11px] text-rose-400 mt-1">{slugError}</div>}
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Status</label>
                  <div className="flex flex-wrap gap-1.5" data-testid="edit-status-group">
                    {enums.statuses.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setField({ tenant_status: s })}
                        disabled={!!t.deleted_at}
                        className={`px-2.5 py-1 rounded-full border text-[10px] font-black uppercase tracking-wider transition-colors ${
                          edit.tenant_status === s ? "bg-white text-black border-white" : "border-white/10 text-white/60 hover:text-white"
                        }`}
                        data-testid={`edit-status-${s.toLowerCase()}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Package</label>
                  <div className="flex flex-wrap gap-1.5" data-testid="edit-package-group">
                    {enums.packages.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setField({ package: p })}
                        disabled={!!t.deleted_at}
                        className={`px-2.5 py-1 rounded-full border text-[10px] font-black uppercase tracking-wider transition-colors ${
                          edit.package === p ? "bg-white text-black border-white" : "border-white/10 text-white/60 hover:text-white"
                        }`}
                        data-testid={`edit-package-${p.toLowerCase()}`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Owner Name</label>
                  <Input
                    value={edit.owner_name}
                    onChange={(e) => setField({ owner_name: e.target.value })}
                    disabled={!!t.deleted_at}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="edit-owner-name"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Owner Email</label>
                  <Input
                    type="email"
                    value={edit.owner_email}
                    onChange={(e) => setField({ owner_email: e.target.value })}
                    disabled={!!t.deleted_at}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="edit-owner-email"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Owner Phone</label>
                  <Input
                    value={edit.owner_phone}
                    onChange={(e) => setField({ owner_phone: e.target.value })}
                    disabled={!!t.deleted_at}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="edit-owner-phone"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Catatan</label>
                  <Textarea
                    value={edit.notes}
                    onChange={(e) => setField({ notes: e.target.value })}
                    disabled={!!t.deleted_at}
                    rows={3}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="edit-notes"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 mt-4">
                <Button
                  onClick={save}
                  disabled={!canSave || saving}
                  className="bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-40"
                  data-testid="btn-save-tenant"
                >
                  {saving ? <><Loader2 size={14} className="mr-1 animate-spin" /> Menyimpan…</> : <><Save size={14} className="mr-1" /> Simpan Perubahan</>}
                </Button>
                {!t.deleted_at && (
                  <Button
                    variant="outline"
                    onClick={() => setShowDelete(true)}
                    className="border-rose-500/30 bg-transparent text-rose-400 hover:bg-rose-500/10 hover:text-rose-300"
                    data-testid="btn-delete-tenant"
                  >
                    <Trash2 size={14} className="mr-1" /> Hapus Tenant
                  </Button>
                )}
              </div>
            </Card>

            {/* Tenant Admin Access */}
            {!t.deleted_at && (
              <Card className="bg-white/5 border-white/10 p-5 rounded-2xl mb-4" data-testid="tenant-admin-access">
                <h3 className="text-sm font-display font-bold text-white mb-3 flex items-center gap-2">
                  <KeyRound size={14} /> Akses Tenant Admin
                </h3>
                <Row label="Admin Email" value={<span className="font-mono">{t.owner_email || "—"}</span>} testId="admin-access-email" />
                {t.tenant_slug && (
                  <Row
                    label="Login URL"
                    value={
                      <a
                        href={`https://${t.tenant_slug}.dynicty.com/admin/login`}
                        target="_blank" rel="noopener noreferrer"
                        className="font-mono text-cyan-300 hover:text-cyan-200 underline"
                        data-testid="admin-access-login-url"
                      >
                        {`https://${t.tenant_slug}.dynicty.com/admin/login`}
                      </a>
                    }
                    testId="admin-access-login-url-row"
                  />
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    onClick={doResetPassword}
                    disabled={resetPwState.loading}
                    variant="outline"
                    className="border-amber-500/40 bg-amber-500/10 text-amber-100 hover:bg-amber-500/20"
                    data-testid="btn-reset-admin-password"
                  >
                    {resetPwState.loading ? (
                      <><Loader2 size={14} className="mr-1 animate-spin" /> Reset…</>
                    ) : (
                      <><KeyRound size={14} className="mr-1" /> Reset Password Admin</>
                    )}
                  </Button>
                </div>

                {resetPwState.open && resetPwState.temp && (
                  <div
                    className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/5 p-3"
                    data-testid="admin-reset-result"
                  >
                    <div className="text-amber-200 text-xs font-black mb-2 flex items-center gap-1">
                      <KeyRound size={12} /> PASSWORD BARU (sekali tampil, simpan sekarang)
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className="flex-1 rounded-lg bg-black/40 border border-amber-500/30 px-3 py-2 font-mono text-sm text-amber-100 break-all select-all"
                        data-testid="admin-reset-password-value"
                      >
                        {resetPwState.showPw ? resetPwState.temp : "•".repeat(Math.min(20, resetPwState.temp.length))}
                      </div>
                      <Button
                        onClick={() => setResetPwState((s) => ({ ...s, showPw: !s.showPw }))}
                        variant="outline"
                        className="border-white/10 bg-white/5 text-white hover:bg-white/10 shrink-0"
                        data-testid="admin-reset-password-toggle"
                      >
                        {resetPwState.showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                      </Button>
                      <Button
                        onClick={() => copyResetPassword(resetPwState.temp, "Password")}
                        variant="outline"
                        className="border-cyan-500/40 bg-cyan-500/10 text-cyan-100 hover:bg-cyan-500/20 shrink-0"
                        data-testid="admin-reset-password-copy"
                      >
                        <Copy size={14} />
                      </Button>
                      <Button
                        onClick={() => setResetPwState({ open: false, loading: false, temp: null, showPw: false })}
                        variant="ghost"
                        className="text-white/60 hover:text-white shrink-0"
                        data-testid="admin-reset-password-close"
                        aria-label="Tutup"
                      >
                        <X size={14} />
                      </Button>
                    </div>
                    <div className="text-[10px] text-amber-200/70 mt-2">
                      Password ini tidak akan bisa ditampilkan lagi. Password lama tidak berlaku.
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* System Information */}
            <Card className="bg-white/5 border-white/10 p-5 rounded-2xl mb-4" data-testid="tenant-system-info">
              <h3 className="text-sm font-display font-bold text-white mb-3 flex items-center gap-2">
                <Info size={14} /> System Information
              </h3>
              <Row label="Tenant ID" value={<span className="font-mono">{t.tenant_id}</span>} testId="sys-tenant-id" />
              <Row label="Tenant Code" value={<span className="font-mono">{t.tenant_code}</span>} testId="sys-tenant-code" />
              <Row label="Status" value={<TenantStatusBadge status={t.tenant_status} size="sm" />} testId="sys-status" />
              <Row label="Created At" value={formatDate(t.created_at)} testId="sys-created-at" />
              <Row label="Updated At" value={formatDate(t.updated_at)} testId="sys-updated-at" />
              {t.deleted_at && <Row label="Deleted At" value={formatDate(t.deleted_at)} testId="sys-deleted-at" />}
              {t.deleted_by && <Row label="Deleted By" value={t.deleted_by} testId="sys-deleted-by" />}
              <div className="text-[11px] text-white/40 mt-3">
                💡 <code className="text-white/70">tenant_id</code> dan <code className="text-white/70">tenant_code</code> bersifat <strong>immutable</strong>.
              </div>
            </Card>

            {/* Feature Flags — override editor */}
            {!t.deleted_at && (
              <Card className="bg-white/5 border-white/10 p-5 rounded-2xl mb-4" data-testid="tenant-feature-flags">
                <h3 className="text-sm font-display font-bold text-white mb-3 flex items-center gap-2">
                  <Flag size={14} /> Feature Flags
                </h3>
                <p className="text-[11px] text-white/50 mb-4">
                  Fitur efektif dihitung dari <strong>Package Mapping</strong> ({t.package}) +{" "}
                  <strong>Tenant Override</strong>. Override menang.
                </p>
                <TenantOverrideEditor tenantId={t.tenant_id} embedded />
              </Card>
            )}

            {/* Template */}
            {!t.deleted_at && <TenantTemplateSection tenantId={t.tenant_id} />}
            {!t.deleted_at && <TenantDesignSwitchSection tenantId={t.tenant_id} />}

            <DeleteTenantDialog
              tenant={t}
              open={showDelete}
              onClose={() => setShowDelete(false)}
              onDeleted={() => {
                setShowDelete(false);
                navigate("/platform/tenants");
              }}
            />
          </>
        )}
      </div>
    </PlatformLayout>
  );
}
